import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context.tsx";
import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { LightningBoltIcon } from "@radix-ui/react-icons";
import { useMemo } from "react";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";
import { getAuthorName } from "@/pages/(app)/returns/utils/get-author-name.ts";

type UpdateTreatementItemProps = {
  activity: ReturnRequestLogDto;
};

type UpdateTreatementItemParams = {
  lineItemId: string;
  quantity: number;
};

export default function UpdateTreatmentItemActivity({
  activity,
}: UpdateTreatementItemProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const currentReturnRequest = useCurrentReturnRequest();

  const author = shop.users.find(
    (user) => user.userId === activity.author,
  )?.user;
  const authorName = getAuthorName(author);

  const getItem = useMemo(
    () => (lineItemId: string) => {
      return currentReturnRequest?.line_items.find(
        (item) => item.line_item_id === lineItemId,
      )!;
    },
    [currentReturnRequest],
  );

  const quantityHandled = (
    activity.params as unknown as UpdateTreatementItemParams[]
  )
    .map((param) => param.quantity)
    .reduce((a, b) => a + b, 0);

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <LightningBoltIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2">
          <Accordion type="single" collapsible>
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="p-0">
                <ActivityItemHeader title="" timestamp={activity.date}>
                  <span className="font-bold">{authorName}</span>
                  {activity.status === "ok"
                    ? t("returnDetail.approvedItem")
                    : t("returnDetail.rejectedItem")}
                  {quantityHandled}{" "}
                  {t("returnDetail.article", { count: quantityHandled })}
                </ActivityItemHeader>
              </AccordionTrigger>
              <AccordionContent>
                {(
                  activity.params as unknown as UpdateTreatementItemParams[]
                ).map((param) => {
                  return (
                    <div
                      key={param.lineItemId}
                      className="flex items-center gap-2 mt-2"
                    >
                      <img
                        src={getItem(param.lineItemId)?.image || undefined}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                      <span className="text-xs">
                        {getItem(param.lineItemId)?.title} x{param.quantity}
                      </span>
                    </div>
                  );
                })}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
